import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import {
  Paginated,
  PaginationQuery,
  RawPaginationMeta,
  mapPaginationMeta,
} from '../../../../core/models/pagination.model';

export interface MenuCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  image?: string | null;
  sort_order: number;
  is_active: boolean;
  items_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface MenuCategoryInput {
  name: string;
  description?: string;
  sort_order?: number;
  is_active?: boolean;
  image?: File | null;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: Record<string, string[]>;
}

@Injectable({ providedIn: 'root' })
export class MenuCategoriesService {
  private readonly apiUrl = `${environment.apiUrl}/menu-categories`;

  constructor(private http: HttpClient) {}

  /** Full (unpaginated) list — used by dropdown consumers (e.g. menu-item form). */
  getAll(): Observable<ApiResponse<MenuCategory[]>> {
    return this.http.get<ApiResponse<MenuCategory[]>>(this.apiUrl, {
      params: new HttpParams().set('all', 'true'),
    });
  }

  /** Paginated list for the menu categories list page. */
  getPage(query: PaginationQuery = {}): Observable<Paginated<MenuCategory>> {
    const params = new HttpParams()
      .set('page', String(query.page ?? 1))
      .set('perPage', String(query.perPage ?? 25));

    return this.http
      .get<ApiResponse<MenuCategory[]> & { meta?: RawPaginationMeta }>(this.apiUrl, {
        params,
      })
      .pipe(
        map((res) => ({
          items: res.data ?? [],
          meta: mapPaginationMeta(res.meta, res.data?.length ?? 0),
        })),
      );
  }

  get(id: string): Observable<ApiResponse<MenuCategory>> {
    return this.http.get<ApiResponse<MenuCategory>>(`${this.apiUrl}/${id}`);
  }

  create(data: MenuCategoryInput): Observable<ApiResponse<MenuCategory>> {
    return this.http.post<ApiResponse<MenuCategory>>(this.apiUrl, this.toFormData(data));
  }

  // Uses the POST {id} multipart alias so image uploads are parsed by Laravel.
  update(id: string, data: MenuCategoryInput): Observable<ApiResponse<MenuCategory>> {
    return this.http.post<ApiResponse<MenuCategory>>(`${this.apiUrl}/${id}`, this.toFormData(data));
  }

  delete(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}`);
  }

  toggleActive(id: string): Observable<ApiResponse<{ id: string; is_active: boolean }>> {
    return this.http.patch<ApiResponse<{ id: string; is_active: boolean }>>(
      `${this.apiUrl}/${id}/toggle-active`,
      {},
    );
  }

  private toFormData(data: MenuCategoryInput): FormData {
    const fd = new FormData();
    fd.append('name', data.name);
    if (data.description != null) fd.append('description', data.description);
    if (data.sort_order != null) fd.append('sort_order', String(data.sort_order));
    // Laravel's `boolean` rule accepts "1"/"0" but not "true"/"false".
    fd.append('is_active', data.is_active ? '1' : '0');
    if (data.image) fd.append('image', data.image);
    return fd;
  }
}
